/**
 * File: assets/basari-api-integration.js
 * Purpose: Connect the contact lens PDP (basari-contact-lens.js) to the Basari
 *          backend for CLEAR lenses: prescription options, price, stock and a
 *          cart that charges the resolved SKU's own Shopify variant.
 *
 * SCOPE — read this first:
 *   This script only acts on a product page whose Shopify product ID is mapped
 *   to an ACTIVE product in the Basari admin (GET /api/storefront/products/<id>
 *   returns 200). For every other product the lookup returns 404 and this
 *   script does nothing: the theme's own options, price, Buy Now, cart icon
 *   and Subscribe behave exactly as before. Colour lens products are left to
 *   the theme.
 *
 * WHAT IT DOES for a mapped clear lens product (spherical, toric, multifocal):
 *   - Prescription modals list only values that exist as switched-on SKUs.
 *     Lists are dependent: CYL is narrowed by the eye's SPH, Axis by SPH + CYL
 *     (ADD by SPH). A value whose SKUs are all at zero stock is shown greyed
 *     with "Out of stock" and cannot be picked. Loading, empty, error and
 *     retry states are shown inside the modal.
 *   - There is no pack to choose on a managed product: the theme's pack block
 *     (theme-editor cards, not product data) is hidden, and the Boxes row says
 *     what one box holds (lenses per box, per eye, from the Basari admin).
 *   - Without Power is not offered (it cannot be bought here yet), and a
 *     product sold for one eye only offers just that eye (needs
 *     basari-contact-lens-eyes.js).
 *   - "Your order" under the eye columns lists each included eye's power,
 *     boxes and quoted price, then the total.
 *   - Each eye is offered its own combinations. The lists the product request
 *     already carries (every SPH list) open with no further request; a list
 *     narrowed by an earlier pick is asked for once when that pick is made and
 *     kept in memory for as long as the page is open.
 *   - Changing SPH clears a CYL/Axis (or ADD) that no longer exists with it;
 *     changing CYL clears an Axis that no longer exists with it.
 *   - Toric Axis: locked with "Select Cylindrical first" until CYL is picked;
 *     set to 0 and locked when CYL is 0; unlocked for any other CYL.
 *   - Price: the family's lowest price ("From ...") until every ticked eye
 *     resolves, then price x boxes summed across the ticked eyes.
 *   - Buy Now and the cart icon add ONE CART LINE PER EYE on the Shopify
 *     variant mapped to that eye's SKU, after a fresh server-side check.
 *     Speciality products use the same purchase path with their typed values.
 *   - Nothing is added when a SKU has no Shopify variant, or when Shopify's
 *     price for the variant differs from the SKU's price.
 *   - Subscribe lens is refused with a message on a managed product: the
 *     theme's Recharge flow would add the generic variant at its own price.
 *
 * The displayed price is informational. What the shopper pays is the price of
 * the Shopify variant in the cart; this script never sends a price to Shopify.
 *
 * DRAFT ORDER CHECKOUT (the product answer says checkout.mode = DRAFT_ORDER):
 *   No variant per SKU is needed. Each eye is still its own cart line, on the
 *   product's own Shopify variant, carrying the theme's prescription
 *   properties plus hidden _basari_sku, _basari_rx (the eye and values picked)
 *   and _basari_price (display only). That variant's Shopify price is NOT what
 *   is charged: basari-cart-guard.js shows the backend's prices in the cart and
 *   sends payment through POST /api/storefront/checkout, which prices every
 *   line again on the server and returns a Shopify Draft Order invoice. Buy
 *   Now goes there too, never to /checkout.
 *
 * SETUP (staging theme first):
 *   1. Upload this file to the theme's assets/ folder.
 *   2. In sections/basari-main-product-contact-lens.liquid add, immediately
 *      AFTER the existing basari-contact-lens.js script tag:
 *        <script src="{{ 'basari-api-integration.js' | asset_url }}" defer="defer"></script>
 *   3. Theme settings > Basari backend > Basari API URL: the backend's https URL.
 */
(function () {
  'use strict';

  // ---- Backend address ------------------------------------------------------
  // Set in the theme editor: Theme settings > Basari backend > Basari API URL
  // (layout/theme.liquid puts it in window.BasariConfig.apiBaseUrl). The
  // fallback below is for local test pages only; leave it empty in the theme.
  // No valid https address = this script calls nothing and leaves the theme as
  // it is (an expired tunnel address used to fail on every product page).
  var API_BASE_URL_FALLBACK = '';
  var API_BASE_URL = basariApiBase(API_BASE_URL_FALLBACK);
  // ------------------------------------------------------------------------

  function basariApiBase(fallback) {
    var configured = window.BasariConfig && window.BasariConfig.apiBaseUrl;
    var raw = String(configured || fallback || '').trim().replace(/\/+$/, '');
    if (!raw) {
      if (window.console) console.warn('[Basari] No backend address is set (Theme settings > Basari backend). Basari pricing is off.');
      return '';
    }
    if (!/^https:\/\/[a-z0-9.-]+(:\d+)?(\/[^\s]*)?$/i.test(raw)) {
      if (window.console) console.warn('[Basari] The backend address must be an https URL; got "' + raw + '". Basari pricing is off.');
      return '';
    }
    return raw;
  }

  var DEBOUNCE_MS = 250;
  var REQUEST_TIMEOUT_MS = 10000;
  var MANAGED_KEY = 'basari:managed:';
  var FIELDS_BY_TYPE = { SPHERICAL: ['sph'], TORIC: ['sph', 'cyl', 'axis'], MULTIFOCAL: ['sph', 'add'] };
  var FIELD_LABEL = { sph: 'Spherical', cyl: 'Cylindrical', axis: 'Axis', add: 'Addl.Power' };
  var EYES = ['right', 'left'];
  var SELECTED_CLASS = 'basari-cl-sph-modal__option--selected';

  var configCache = {}; // shopifyProductId -> config | null (not managed)
  var controllers = [];

  // ---- Network -----------------------------------------------------------
  function fetchJson(url, options) {
    var opts = options || {};
    // ngrok's free tier answers browser-looking requests with an HTML
    // interstitial unless this header is present. Ignored by a real domain.
    opts.headers = opts.headers || {};
    opts.headers['ngrok-skip-browser-warning'] = 'true';
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = null;
    if (controller) {
      opts.signal = controller.signal;
      timer = window.setTimeout(function () {
        controller.abort();
      }, REQUEST_TIMEOUT_MS);
    }
    return fetch(url, opts)
      .then(function (res) {
        return res.text().then(function (text) {
          var body = null;
          try {
            body = text ? JSON.parse(text) : null;
          } catch (e) {
            body = null;
          }
          return { ok: res.ok, status: res.status, body: body };
        });
      })
      .catch(function () {
        return { ok: false, status: 0, body: null };
      })
      .then(function (result) {
        if (timer) window.clearTimeout(timer);
        return result;
      });
  }

  function postJson(path, body) {
    return fetchJson(API_BASE_URL + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
  }

  function cartRoot() {
    return window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/';
  }

  function parseConfigJson(root) {
    var node = root.querySelector('[data-basari-cl-config]');
    if (!node) return {};
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      return {};
    }
  }

  function debounce(fn, ms) {
    var timer = null;
    return function () {
      if (timer) window.clearTimeout(timer);
      timer = window.setTimeout(fn, ms);
    };
  }

  function sameNumber(a, b) {
    if (a === '' || a == null || b === '' || b == null) return false;
    var x = Number(a);
    var y = Number(b);
    return !isNaN(x) && !isNaN(y) ? x === y : String(a) === String(b);
  }

  function isZero(value) {
    return value !== '' && value != null && !isNaN(Number(value)) && Number(value) === 0;
  }

  // ---- Price display -------------------------------------------------
  // Same selector contract as basari-product-variants.js, so one price block
  // updates whether the change came from a swatch or a prescription.
  function findPriceDom(scope) {
    var priceRoot = (scope || document).querySelector('[data-basari-product-price]');
    if (!priceRoot) return null;
    return {
      priceRoot: priceRoot,
      priceCurrent: priceRoot.querySelector('[data-basari-product-price-current]'),
      priceCurrentValue: priceRoot.querySelector(
        '[data-basari-product-price-current] .basari-product-price__current-value'
      ),
      priceCompare: priceRoot.querySelector('[data-basari-product-price-compare]'),
      priceCompareValue: priceRoot.querySelector(
        '[data-basari-product-price-compare] .basari-product-price__compare-value'
      ),
      priceSavings: priceRoot.querySelector('[data-basari-product-price-savings]')
    };
  }

  // Prices are written the way the theme writes them ("Dhs. 309.00"). The
  // pattern is read from the price the theme rendered, and only used when the
  // shopper is browsing in the backend's currency: in another market Shopify
  // converts its own prices, ours are not, so they are labelled with their
  // currency code instead of borrowing a converted price's symbol.
  var moneyPattern = null;
  function learnMoneyPattern(scope, currency) {
    if (moneyPattern !== null) return;
    moneyPattern = false;
    var active = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
    if (active && currency && String(active).toUpperCase() !== String(currency).toUpperCase()) return;
    var el = (scope || document).querySelector('[data-basari-product-price-current] .basari-product-price__current-value');
    var text = el ? String(el.textContent || '').replace(/\s+/g, ' ').trim() : '';
    var m = text.match(/^(\D*?)(\d[\d.,\s]*\d|\d)(\D*)$/);
    if (!m) return;
    var number = m[2];
    var comma = /,\d{2}$/.test(number) && number.indexOf('.') === -1;
    moneyPattern = { prefix: m[1], suffix: m[3], decimal: comma ? ',' : '.', group: comma ? '.' : ',' };
  }

  function formatMoney(amount, currency) {
    var n = Number(amount);
    if (isNaN(n)) return '';
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function' && window.Shopify.money_format) {
      try {
        return window.Shopify.formatMoney(Math.round(n * 100), window.Shopify.money_format);
      } catch (e) {
        /* fall through */
      }
    }
    if (moneyPattern) {
      var parts = n.toFixed(2).split('.');
      var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, moneyPattern.group);
      return moneyPattern.prefix + whole + moneyPattern.decimal + parts[1] + moneyPattern.suffix;
    }
    return currency + ' ' + n.toFixed(2);
  }

  function round2(n) {
    return Math.round(Number(n) * 100) / 100;
  }

  // The order summary's look, added once per page. It uses the theme's own
  // contact lens colours (basari-contact-lens.css) where they exist, so the
  // summary needs no change to the theme's stylesheet.
  function injectSummaryStyles() {
    if (document.getElementById('basari-api-summary-styles')) return;
    var style = document.createElement('style');
    style.id = 'basari-api-summary-styles';
    style.textContent = [
      '.basari-api-summary{margin:1rem 0 0;padding:1rem 1.15rem;border:1px solid var(--basari-cl-border,#e5e7eb);border-radius:.85rem;background:var(--basari-cl-surface,#fff);font-size:1.3rem;line-height:1.4;color:var(--basari-cl-text,#111827)}',
      '.basari-api-summary[hidden]{display:none}',
      '.basari-api-summary__list{margin:0;padding:0;list-style:none}',
      '.basari-api-summary__row{display:flex;align-items:flex-start;justify-content:space-between;gap:1rem;padding:.6rem 0;border-top:1px solid var(--basari-cl-border,#e5e7eb)}',
      '.basari-api-summary__row:first-child{border-top:0;padding-top:0}',
      '.basari-api-summary__text{display:flex;flex-direction:column;gap:.15rem;min-width:0}',
      '.basari-api-summary__eye{font-weight:600}',
      '.basari-api-summary__detail{color:var(--basari-cl-muted,#6b7280);overflow-wrap:anywhere;font-variant-numeric:tabular-nums}',
      '.basari-api-summary__note{font-size:1.2rem;font-weight:500}',
      '.basari-api-summary__row[data-state="needs"] .basari-api-summary__note{color:#92400e}',
      '.basari-api-summary__row[data-state="checking"] .basari-api-summary__note{color:var(--basari-cl-muted,#6b7280)}',
      '.basari-api-summary__row[data-state="unavailable"] .basari-api-summary__note,.basari-api-summary__row[data-orderable="false"] .basari-api-summary__note{color:#b42318}',
      '.basari-api-summary__amount{flex:0 0 auto;display:flex;gap:.5rem;align-items:baseline;font-weight:600;font-variant-numeric:tabular-nums;white-space:nowrap}',
      '.basari-api-summary__compare{font-weight:400;color:var(--basari-cl-muted,#6b7280)}',
      '.basari-api-summary__total{display:flex;align-items:baseline;gap:.6rem;margin:.6rem 0 0;padding-top:.7rem;border-top:1px solid var(--basari-cl-border,#e5e7eb);font-variant-numeric:tabular-nums}',
      '.basari-api-summary__total>span:first-child{margin-right:auto;font-weight:600}',
      '.basari-api-summary__total strong{font-size:1.5rem}',
      '.basari-api-summary__total[data-state="pending"]>span{font-weight:400;color:var(--basari-cl-muted,#6b7280)}'
    ].join('\n');
    document.head.appendChild(style);
  }

  // The price block exactly as the theme rendered it (the Shopify price), so it
  // can be put back when no Base Price is set and nothing is chosen yet.
  function snapshotPrice(scope) {
    var dom = findPriceDom(scope);
    if (!dom || !dom.priceRoot) return null;
    // Text is kept for the leaf elements only; the two wrappers keep just
    // whether they were shown (writing text into a wrapper would wipe the
    // elements inside it).
    var leaves = [dom.priceCurrentValue, dom.priceCompareValue];
    if (dom.priceSavings) {
      leaves.push(dom.priceSavings.querySelector('[data-basari-product-price-badge]'));
      leaves.push(dom.priceSavings.querySelector('[data-basari-product-price-save-text]'));
      leaves.push(dom.priceSavings.querySelector('.visually-hidden'));
    }
    var entries = leaves.map(function (node) {
      return node ? { node: node, text: node.textContent, hidden: node.hasAttribute('hidden') } : null;
    });
    [dom.priceCompare, dom.priceSavings].forEach(function (node) {
      if (node) entries.push({ node: node, text: null, hidden: node.hasAttribute('hidden') });
    });
    return {
      onSale: dom.priceRoot.getAttribute('data-on-sale'),
      onSaleClass: dom.priceRoot.classList.contains('basari-product-price--on-sale'),
      price: dom.priceCurrent ? dom.priceCurrent.getAttribute('data-price') : null,
      nodes: entries
    };
  }

  function restorePrice(scope, snap) {
    var dom = findPriceDom(scope);
    if (!dom || !dom.priceRoot || !snap) return;
    dom.priceRoot.setAttribute('data-basari-resolved', 'false');
    if (snap.onSale === null) dom.priceRoot.removeAttribute('data-on-sale');
    else dom.priceRoot.setAttribute('data-on-sale', snap.onSale);
    dom.priceRoot.classList.toggle('basari-product-price--on-sale', snap.onSaleClass);
    if (dom.priceCurrent && snap.price !== null) dom.priceCurrent.setAttribute('data-price', snap.price);
    snap.nodes.forEach(function (entry) {
      if (!entry) return;
      if (entry.text !== null) entry.node.textContent = entry.text;
      if (entry.hidden) entry.node.setAttribute('hidden', '');
      else entry.node.removeAttribute('hidden');
    });
  }

  // Writes a price into the theme's price block, with its savings recalculated
  // from the same two numbers, or hidden when there is no real saving.
  function updatePriceDisplay(scope, amount, compareAt, currency, prefix) {
    var dom = findPriceDom(scope);
    if (!dom || !dom.priceRoot) return;
    var onSale = compareAt != null && Number(compareAt) > Number(amount);

    dom.priceRoot.setAttribute('data-basari-resolved', prefix ? 'false' : 'true');
    dom.priceRoot.setAttribute('data-on-sale', onSale ? 'true' : 'false');
    dom.priceRoot.classList.toggle('basari-product-price--on-sale', onSale);

    if (dom.priceCurrentValue) dom.priceCurrentValue.textContent = (prefix || '') + formatMoney(amount, currency);
    if (dom.priceCurrent) dom.priceCurrent.setAttribute('data-price', String(Math.round(Number(amount) * 100)));

    if (dom.priceCompare) {
      if (onSale) {
        dom.priceCompare.removeAttribute('hidden');
        if (dom.priceCompareValue) dom.priceCompareValue.textContent = formatMoney(compareAt, currency);
      } else {
        dom.priceCompare.setAttribute('hidden', '');
      }
    }
    if (dom.priceSavings) {
      if (onSale) {
        var save = round2(Number(compareAt) - Number(amount));
        var percent = Math.round((save / Number(compareAt)) * 100);
        var badge = dom.priceSavings.querySelector('[data-basari-product-price-badge]');
        var saveText = dom.priceSavings.querySelector('[data-basari-product-price-save-text]');
        var spoken = dom.priceSavings.querySelector('.visually-hidden');
        if (badge) badge.textContent = percent + '% off';
        if (saveText) saveText.textContent = 'save ' + formatMoney(save, currency);
        if (spoken) spoken.textContent = 'You save ' + percent + ' percent, ' + formatMoney(save, currency);
        dom.priceSavings.removeAttribute('hidden');
      } else {
        dom.priceSavings.setAttribute('hidden', '');
      }
    }
  }

  // ---- Per-product controller ------------------------------------------
  function createController(root, shopifyProductId, cfg) {
    var lensType = cfg.lensType;
    var fields = FIELDS_BY_TYPE[lensType] || [];
    var isSpeciality = lensType === 'SPECIALITY';
    var busy = false;
    var destroyed = false;
    var priceSeq = 0;
    var syncSeq = { right: 0, left: 0 };
    var optionsCache = {};
    var observers = [];
    var listeners = [];
    // The price the theme rendered (the Shopify price), before anything here
    // changes it, and the way the theme writes money.
    learnMoneyPattern(root, cfg.currency);
    var shopifyPrice = snapshotPrice(root);

    // DRAFT_ORDER: the backend charges the SKU's price on a Shopify Draft
    // Order, so a power needs no Shopify variant of its own. VARIANT (the
    // older flow): each SKU must sit on its own variant at the same price.
    function draftMode() {
      return !!(cfg.checkout && cfg.checkout.mode === 'DRAFT_ORDER');
    }

    function on(target, name, handler, capture) {
      target.addEventListener(name, handler, !!capture);
      listeners.push([target, name, handler, !!capture]);
    }

    // ---- DOM lookups (same attributes basari-contact-lens.js uses) -------
    function eyeNode(selector, eye) {
      var nodes = root.querySelectorAll(selector);
      for (var i = 0; i < nodes.length; i += 1) {
        if (nodes[i].getAttribute('data-eye') === eye) return nodes[i];
      }
      return null;
    }
    function inputFor(field, eye) {
      return eyeNode('[data-basari-cl-rx-field-input][data-field="' + field + '"]', eye);
    }
    function triggerFor(field, eye) {
      return field === 'sph'
        ? eyeNode('[data-basari-cl-sph-trigger]', eye)
        : eyeNode('[data-basari-cl-rx-trigger][data-field="' + field + '"]', eye);
    }
    function valueOf(field, eye) {
      var input = inputFor(field, eye);
      return input ? String(input.value || '').trim() : '';
    }
    function eyeTicked(eye) {
      var box = eyeNode('[data-basari-cl-power-eye-input]', eye);
      return box ? !!box.checked : true;
    }

    // Mirrors the theme's setRxLiveValue / setSphLiveValue without dispatching,
    // so clearing a dependent value cannot start an event loop.
    function setValue(field, eye, value) {
      var input = inputFor(field, eye);
      var trigger = triggerFor(field, eye);
      var label = trigger
        ? trigger.querySelector(field === 'sph' ? '[data-basari-cl-sph-trigger-label]' : '[data-basari-cl-rx-trigger-label]')
        : null;
      if (input) input.value = value;
      if (trigger) trigger.setAttribute(field === 'sph' ? 'data-sph-value' : 'data-rx-value', value);
      if (label) label.textContent = value || (trigger && trigger.getAttribute('data-placeholder')) || 'Select';
    }

    function setLocked(node, locked) {
      if (!node) return;
      node.disabled = !!locked;
      if (locked) {
        node.setAttribute('aria-disabled', 'true');
        node.setAttribute('tabindex', '-1');
        node.style.opacity = '0.55';
      } else {
        node.removeAttribute('aria-disabled');
        node.removeAttribute('tabindex');
        node.style.opacity = '';
      }
    }

    // One line of help under a control, announced politely.
    function setHint(field, eye, text) {
      var trigger = triggerFor(field, eye);
      if (!trigger || !trigger.parentNode) return;
      var hint = eyeNode('[data-basari-api-hint][data-field="' + field + '"]', eye);
      if (!hint && text) {
        hint = document.createElement('p');
        hint.setAttribute('data-basari-api-hint', '');
        hint.setAttribute('data-field', field);
        hint.setAttribute('data-eye', eye);
        hint.setAttribute('aria-live', 'polite');
        hint.style.cssText = 'margin:4px 0 0;font-size:12px;line-height:1.3;color:#6b7280;';
        trigger.parentNode.insertBefore(hint, trigger.nextSibling);
      }
      if (hint) {
        hint.textContent = text || '';
        hint.hidden = !text;
      }
    }

    function showMessage(text) {
      var box = root.querySelector('[data-basari-api-message]');
      if (!box) {
        box = document.createElement('p');
        box.setAttribute('data-basari-api-message', '');
        box.setAttribute('role', 'alert');
        box.style.cssText = 'margin:8px 0 0;font-size:13px;line-height:1.4;color:#b42318;white-space:pre-line;';
        (root.querySelector('[data-basari-cl-purchase]') || root).appendChild(box);
      }
      box.textContent = text || '';
      box.hidden = !text;
    }

    // ---- Order summary ----------------------------------------------------
    // One line for each eye in the order (an eye that is not chosen is not
    // listed): the power picked for it, its boxes and their price, then the
    // total. It sits directly under the eye columns, so pack, prescription,
    // quantity and price read as one purchase.
    //
    // Prices come from two places. The product's price table (pricesByEye)
    // prices a pick the moment it is made, with no request. The quote, asked
    // for a moment later, has the last word: when it has answered for the
    // current selection its prices and refusals replace the table's. Nothing is
    // calculated here beyond price x boxes, and the quote is asked again at
    // purchase.
    var EYE_TITLE = { right: 'Right eye (OD)', left: 'Left eye (OS)' };
    var PICK_KEYS = ['SPH', 'CYL', 'AXIS', 'ADD'];
    var lastQuote = null;
    var lastQuoteKey = '';

    function selectionKey(sel) {
      return JSON.stringify(sel || readSelection());
    }

    function summaryBox() {
      var box = root.querySelector('[data-basari-api-summary]');
      if (box) return box;
      var anchor = root.querySelector('[data-basari-cl-power]');
      if (!anchor || !anchor.parentNode) return null;
      injectSummaryStyles();
      box = document.createElement('div');
      box.className = 'basari-api-summary';
      box.setAttribute('data-basari-api-summary', '');
      box.setAttribute('role', 'region');
      box.setAttribute('aria-label', 'Your order');
      box.setAttribute('aria-live', 'polite');
      anchor.parentNode.insertBefore(box, anchor.nextSibling);
      return box;
    }

    function node(tag, cls, text) {
      var n = document.createElement(tag);
      if (cls) n.className = cls;
      if (text != null) n.textContent = text;
      return n;
    }

    function boxesText(n) {
      return n + (Number(n) === 1 ? ' box' : ' boxes');
    }

    function axisNeeded(picks) {
      return !(lensType === 'TORIC' && isZero(picks.cyl));
    }

    // The price table's entry for one eye's picks: an entry, null (this eye
    // is not sold in that power), or undefined (no table: ask the quote).
    function lookupPrice(eye, picks) {
      var table = cfg.pricesByEye && cfg.pricesByEye[eye];
      if (!table) return undefined;
      for (var i = 0; i < table.length; i += 1) {
        var entry = table[i];
        var match = fields.every(function (f) {
          if (f === 'axis' && !axisNeeded(picks)) return true;
          var want = picks[f];
          var have = entry[f];
          if (!want && have == null) return true;
          return sameNumber(have, want) || (have != null && want != null && String(have).toUpperCase() === String(want).toUpperCase());
        });
        if (match) return entry;
      }
      return null;
    }

    // Each included eye's state right now.
    function buildView() {
      var sel = readSelection();
      var fresh = lastQuote && lastQuoteKey === selectionKey(sel) ? lastQuote : null;
      var qLines = fresh && fresh.valid ? fresh.lines || [] : [];
      var qErrors = (fresh && fresh.errors) || [];
      var sold = soldEyes();
      var view ={ items: [], total: 0, compareTotal: 0, complete: true, fromQuote: !!(fresh && fresh.valid) };

      EYES.forEach(function (eye) {
        if (sold[eye] === false || !sel[eye] || !sel[eye].enabled) return;
        var picks = picked(eye);
        var boxes = Number(valueOf('boxes', eye) || 1) || 1;
        var item = { eye: eye, picks: picks, boxes: boxes, state: 'needs', note: '', orderable: true, stock: null };
        var missing = fields.filter(function (f) {
          return !picks[f] && (f !== 'axis' || axisNeeded(picks));
        })[0];
        var line = qLines.filter(function (l) { return l.eye === eye; })[0];
        var error = qErrors.filter(function (e) { return e.eye === eye; })[0];

        if (missing) {
          item.note = 'Choose ' + FIELD_LABEL[missing];
        } else if (line) {
          item.state = 'priced';
          item.unit = Number(line.unitPrice);
          item.compare = line.unitCompareAtPrice != null && Number(line.unitCompareAtPrice) > item.unit ? Number(line.unitCompareAtPrice) : null;
          item.lineTotal = Number(line.lineTotal);
          item.stock = line.stock;
          item.orderable = draftMode() || (!!line.variantId && !priceDiffers(line));
        } else if (error && error.field !== 'boxes') {
          item.state = 'unavailable';
          item.note = error.message;
        } else {
          var entry = lookupPrice(eye, picks);
          if (entry === undefined) {
            item.state = 'checking';
            item.note = 'Checking price…';
          } else if (entry === null) {
            item.state = 'unavailable';
            item.note = 'This power is not available for this eye';
          } else if (!(entry.stock > 0)) {
            item.state = 'unavailable';
            item.note = 'Out of stock';
            item.stock = 0;
          } else if (entry.stock < boxes) {
            item.state = 'unavailable';
            item.note = 'Only ' + boxesText(entry.stock) + ' in stock';
            item.stock = entry.stock;
          } else {
            item.state = 'priced';
            item.unit = Number(entry.price);
            item.compare = entry.compareAtPrice != null && Number(entry.compareAtPrice) > item.unit ? Number(entry.compareAtPrice) : null;
            item.lineTotal = round2(item.unit * boxes);
            item.stock = entry.stock;
            item.orderable = !!entry.orderable;
          }
        }
        if (item.state === 'priced') {
          view.total = round2(view.total + item.lineTotal);
          view.compareTotal = round2(view.compareTotal + (item.compare || item.unit) * boxes);
          if (!item.orderable) item.note = 'Not available to order online yet';
        } else {
          view.complete = false;
        }
        view.items.push(item);
      });
      if (!view.items.length) view.complete = false;
      // The quote's own totals, when it has priced this exact selection.
      if (view.complete && view.fromQuote) {
        view.total = Number(fresh.total);
        view.compareTotal = fresh.compareAtTotal != null ? Number(fresh.compareAtTotal) : view.total;
      }
      view.blocked = view.items.some(function (i) { return i.state === 'priced' && !i.orderable; });
      return view;
    }

    function picksText(item) {
      var out = [];
      fields.forEach(function (f) {
        if (item.picks[f] && (f !== 'axis' || axisNeeded(item.picks))) out.push(PICK_KEYS[['sph', 'cyl', 'axis', 'add'].indexOf(f)] + ' ' + item.picks[f]);
      });
      return out;
    }

    function renderSummary(view) {
      var box = summaryBox();
      if (!box) return;
      // Without Power has its own flow in the theme; this summary is for
      // prescription lenses.
      if (withoutPowerSelected() || !view.items.length) {
        box.hidden = true;
        return;
      }
      box.hidden = false;
      var currency = cfg.currency;
      var qty = cfg.packQuantity || {};
      var packsDiffer = Number(qty.right) > 0 && Number(qty.left) > 0 && Number(qty.right) !== Number(qty.left);

      box.innerHTML = '';
      var list = node('ul', 'basari-api-summary__list');
      view.items.forEach(function (item) {
        var row = node('li', 'basari-api-summary__row');
        row.setAttribute('data-eye', item.eye);
        row.setAttribute('data-state', item.state);
        if (item.state === 'priced') row.setAttribute('data-orderable', item.orderable ? 'true' : 'false');
        var text = node('span', 'basari-api-summary__text');
        text.appendChild(node('span', 'basari-api-summary__eye', EYE_TITLE[item.eye]));
        var parts = picksText(item);
        if (item.state === 'priced') parts.push(boxesText(item.boxes) + ' × ' + formatMoney(item.unit, currency));
        else if (item.state !== 'needs') parts.push(boxesText(item.boxes));
        if (packsDiffer && qty[item.eye]) parts.push(qty[item.eye] + ' lenses per box');
        if (parts.length) text.appendChild(node('span', 'basari-api-summary__detail', parts.join(' · ')));
        if (item.note) text.appendChild(node('span', 'basari-api-summary__note', item.note));
        var amount = node('span', 'basari-api-summary__amount');
        if (item.state === 'priced') {
          if (item.compare) {
            var was = node('s', 'basari-api-summary__compare', formatMoney(round2(item.compare * item.boxes), currency));
            was.setAttribute('aria-label', 'was ' + was.textContent);
            amount.appendChild(was);
            amount.appendChild(document.createTextNode(' '));
          }
          amount.appendChild(document.createTextNode(formatMoney(item.lineTotal, currency)));
        }
        row.appendChild(text);
        row.appendChild(amount);
        list.appendChild(row);
      });
      box.appendChild(list);

      var total = node('p', 'basari-api-summary__total');
      if (view.complete) {
        total.appendChild(node('span', null, 'Total'));
        total.appendChild(document.createTextNode(' '));
        if (view.compareTotal > view.total) {
          var wasTotal = node('s', 'basari-api-summary__compare', formatMoney(view.compareTotal, currency));
          wasTotal.setAttribute('aria-label', 'was ' + wasTotal.textContent);
          total.appendChild(wasTotal);
          total.appendChild(document.createTextNode(' '));
        }
        total.appendChild(node('strong', null, formatMoney(view.total, currency)));
      } else {
        total.setAttribute('data-state', 'pending');
        var waiting = view.items.some(function (i) { return i.state === 'unavailable'; })
          ? 'Change the power marked above to continue'
          : view.items.length > 1
            ? 'The total appears once both eyes are complete'
            : 'The total appears once the power is chosen';
        total.appendChild(node('span', null, waiting));
      }
      box.appendChild(total);
    }

    // ---- Options from the backend ---------------------------------------
    function picked(eye) {
      var out = {};
      for (var i = 0; i < fields.length; i += 1) out[fields[i]] = valueOf(fields[i], eye) || null;
      return out;
    }

    // Dependent option lists for one eye's current picks. Kept in memory for
    // as long as this product page is open (the cache belongs to this product's
    // controller), so a list is asked for once. Callers that ask at the same
    // time share one request, and a failed request is not kept. The quote at
    // purchase time remains the final check of stock.
    function optionsKey(eye, sel) {
      return [eye, sel.sph || '', sel.cyl || ''].join('|');
    }
    function loadOptions(eye, sel) {
      var key = optionsKey(eye, sel);
      var hit = optionsCache[key];
      if (hit) return hit.promise;
      var entry = { value: null, promise: null };
      entry.promise = postJson('/api/storefront/options', {
        shopifyProductId: shopifyProductId,
        eye: eye,
        sph: sel.sph || null,
        cyl: sel.cyl || null
      }).then(function (result) {
        if (!result.ok || !result.body || !result.body.data || !result.body.data.fields) {
          delete optionsCache[key];
          throw new Error('options');
        }
        entry.value = result.body.data.fields;
        return entry.value;
      });
      optionsCache[key] = entry;
      return entry.promise;
    }

    // The list a modal needs, when it is already in memory: the product's own
    // data for a list nothing narrows (every SPH list), or the answer to an
    // earlier request for the same picks. null = it has to be fetched.
    function knownOptions(field, eye, sel) {
      if (field === 'sph' || !sel.sph) {
        // Only this eye's own list. The product's combined list would offer
        // one eye the other eye's powers, so without a per-eye list the eye's
        // options are asked for instead (loadOptions sends the eye).
        var own = cfg.fieldsByEye && cfg.fieldsByEye[eye];
        return own && own[field] ? own[field] : null;
      }
      var hit = optionsCache[optionsKey(eye, { sph: sel.sph, cyl: sel.cyl })];
      return hit && hit.value ? hit.value[field] || [] : null;
    }

    function findOption(list, value) {
      if (!list || !value) return null;
      for (var i = 0; i < list.length; i += 1) {
        if (sameNumber(list[i].value, value) || list[i].value === String(value).toUpperCase()) return list[i];
      }
      return null;
    }

    // ---- Modal lists -----------------------------------------------------
    function modalFor(field) {
      return field === 'sph'
        ? root.querySelector('[data-basari-cl-sph-modal]')
        : root.querySelector('[data-basari-cl-rx-modal][data-field="' + field + '"]');
    }
    function listsOf(modal, field) {
      return field === 'sph'
        ? [modal.querySelector('[data-basari-cl-sph-list="negative"]'), modal.querySelector('[data-basari-cl-sph-list="positive"]')]
        : [modal.querySelector('[data-basari-cl-rx-list]')];
    }

    // Labels the theme rendered for its own options (e.g. the ADD band text),
    // remembered before the list is first replaced.
    var themeLabels = {};
    function rememberLabels(modal, field) {
      if (themeLabels[field]) return;
      themeLabels[field] = {};
      var nodes = modal.querySelectorAll('[data-basari-cl-rx-option]');
      for (var i = 0; i < nodes.length; i += 1) {
        var label = nodes[i].querySelector('.basari-cl-sph-modal__option-label');
        themeLabels[field][nodes[i].getAttribute('data-rx-value')] = label ? label.textContent : '';
      }
    }

    // The value string the theme itself would have used for this option, so
    // its own validation and highlighting keep working.
    function themeValue(field, value) {
      if (field === 'cyl' && isZero(value)) return '0';
      return value;
    }

    // Same element shape as the theme's createSphOption / createRxOption.
    function buildOption(field, option, current) {
      var value = themeValue(field, option.value);
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'basari-cl-sph-modal__option';
      btn.setAttribute('role', 'radio');
      btn.setAttribute(field === 'sph' ? 'data-basari-cl-sph-option' : 'data-basari-cl-rx-option', '');
      btn.setAttribute(field === 'sph' ? 'data-sph-value' : 'data-rx-value', value);
      var selected = sameNumber(value, current) || value === current;
      btn.setAttribute('aria-checked', selected ? 'true' : 'false');
      btn.setAttribute('tabindex', selected ? '0' : '-1');
      if (selected) btn.classList.add(SELECTED_CLASS);

      var radio = document.createElement('span');
      radio.className = 'basari-cl-sph-modal__radio';
      radio.setAttribute('aria-hidden', 'true');
      var text = document.createElement('span');
      text.className = 'basari-cl-sph-modal__option-label';
      text.textContent = (themeLabels[field] && themeLabels[field][value]) || value;
      btn.appendChild(radio);
      btn.appendChild(text);

      if (!option.available) {
        // Every SKU with this value is at zero stock: visible, not selectable,
        // and it says why.
        btn.disabled = true;
        btn.setAttribute('aria-disabled', 'true');
        btn.setAttribute('data-basari-out-of-stock', '');
        btn.classList.add('basari-cl-sph-modal__option--unavailable');
        btn.style.opacity = '0.55';
        btn.style.cursor = 'not-allowed';
        var tag = document.createElement('span');
        tag.className = 'basari-cl-sph-modal__option-note';
        tag.textContent = 'Out of stock';
        tag.style.cssText = 'margin-left:auto;font-size:11px;white-space:nowrap;';
        btn.appendChild(tag);
      }
      return btn;
    }

    function note(text, withRetry, retry) {
      var wrap = document.createElement('div');
      wrap.setAttribute('data-basari-api-state', withRetry ? 'error' : 'empty');
      wrap.setAttribute('role', withRetry ? 'alert' : 'status');
      wrap.style.cssText = 'padding:16px 8px;font-size:13px;line-height:1.4;color:#4b5563;text-align:center;';
      wrap.appendChild(document.createTextNode(text));
      if (withRetry) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.setAttribute('data-basari-api-retry', '');
        btn.textContent = 'Try again';
        btn.style.cssText = 'display:block;margin:10px auto 0;padding:6px 14px;border:1px solid #9ca3af;border-radius:8px;background:#fff;font-size:13px;cursor:pointer;';
        btn.addEventListener('click', function (event) {
          event.stopPropagation();
          retry();
        });
        wrap.appendChild(btn);
      }
      return wrap;
    }

    function renderLoading(lists) {
      lists.forEach(function (list) {
        if (!list) return;
        list.innerHTML = '';
        list.setAttribute('aria-busy', 'true');
        for (var i = 0; i < 4; i += 1) {
          var row = document.createElement('div');
          row.setAttribute('data-basari-api-state', 'loading');
          row.setAttribute('aria-hidden', 'true');
          row.style.cssText = 'height:40px;margin:0 0 8px;border-radius:8px;background:#e5e7eb;';
          list.appendChild(row);
        }
      });
    }

    function renderOptions(modal, field, eye, options) {
      var lists = listsOf(modal, field);
      var current = valueOf(field, eye);
      lists.forEach(function (list) {
        if (!list) return;
        list.innerHTML = '';
        list.removeAttribute('aria-busy');
      });
      if (!options.length) {
        if (lists[0]) lists[0].appendChild(note('No options are available for this selection.', false));
        return;
      }
      if (field === 'sph') {
        // Negative from -0.25 downward; positive from 0.00 upward (handoff 6.1).
        var negative = options.filter(function (o) { return Number(o.value) < 0; }).sort(function (a, b) { return Number(b.value) - Number(a.value); });
        var positive = options.filter(function (o) { return Number(o.value) >= 0; }).sort(function (a, b) { return Number(a.value) - Number(b.value); });
        negative.forEach(function (o) { if (lists[0]) lists[0].appendChild(buildOption(field, o, current)); });
        positive.forEach(function (o) { if (lists[1]) lists[1].appendChild(buildOption(field, o, current)); });
        // A column with nothing in it says so, instead of looking broken.
        [[negative, lists[0]], [positive, lists[1]]].forEach(function (pair) {
          if (pair[0].length || !pair[1]) return;
          var empty = document.createElement('p');
          empty.className = 'basari-cl-sph-modal__empty';
          empty.setAttribute('data-basari-api-empty-column', '');
          empty.style.cssText = 'margin:0;padding:12px 6px;font-size:12px;line-height:1.4;color:#6b7280;text-align:center;';
          empty.textContent = 'None available for this eye';
          pair[1].appendChild(empty);
        });
      } else {
        options.forEach(function (o) { lists[0].appendChild(buildOption(field, o, current)); });
      }
      var focus = modal.querySelector('.' + SELECTED_CLASS) || modal.querySelector('[role="radio"]:not([disabled])');
      if (focus) {
        focus.setAttribute('tabindex', '0');
        if (typeof focus.focus === 'function') focus.focus();
      }
    }

    // A modal just became visible: replace the theme's static list with what
    // the backend says is valid for this eye right now.
    function fillModal(modal, field) {
      var eye = modal.getAttribute('data-active-eye');
      if (!eye) return;
      rememberLabels(modal, field);
      var token = (modal._basariApiToken || 0) + 1;
      modal._basariApiToken = token;
      var lists = listsOf(modal, field);
      var sel = picked(eye);
      var known = knownOptions(field, eye, sel);
      if (known) {
        // Already in memory: shown at once, with no loading state and no request.
        renderOptions(modal, field, eye, known);
        return;
      }
      renderLoading(lists);

      // A field is only narrowed by the fields before it; the same request
      // answers CYL and Axis, so it is the one made when the pick changed.
      var query = { sph: field === 'sph' ? null : sel.sph, cyl: field === 'sph' ? null : sel.cyl };
      loadOptions(eye, query).then(
        function (byField) {
          // Ignore the answer if the modal was closed, reopened for the other
          // eye, or asked again in the meantime.
          if (destroyed || modal._basariApiToken !== token || modal.hasAttribute('hidden')) return;
          if (modal.getAttribute('data-active-eye') !== eye) return;
          renderOptions(modal, field, eye, byField[field] || []);
        },
        function () {
          if (destroyed || modal._basariApiToken !== token || modal.hasAttribute('hidden')) return;
          lists.forEach(function (list) {
            if (!list) return;
            list.innerHTML = '';
            list.removeAttribute('aria-busy');
          });
          if (lists[0]) {
            lists[0].appendChild(
              note('Could not load the options.', true, function () {
                fillModal(modal, field);
              })
            );
          }
        }
      );
    }

    function watchModals() {
      if (typeof MutationObserver !== 'function') return;
      fields.forEach(function (field) {
        var modal = modalFor(field);
        if (!modal) return;
        var observer = new MutationObserver(function () {
          if (!modal.hasAttribute('hidden')) {
            if (!modal._basariApiOpen) {
              modal._basariApiOpen = true;
              fillModal(modal, field);
            }
          } else {
            modal._basariApiOpen = false;
          }
        });
        observer.observe(modal, { attributes: true, attributeFilter: ['hidden', 'data-active-eye'] });
        observers.push(observer);
        if (!modal.hasAttribute('hidden')) fillModal(modal, field);
      });
    }

    // ---- Pack size --------------------------------------------------------
    // The real pack size, stated once. Nothing is selectable here: a managed
    // product is sold by the box, and the number of boxes is chosen per eye.
    function applyPackInfo() {
      // A managed product has no pack to choose: it is sold by the box, and
      // the number of boxes is chosen per eye. The theme's pack block (a
      // heading over an empty list, or theme-editor cards that change nothing)
      // is hidden, and the Boxes row says what one box holds.
      var pack = root.querySelector('[data-basari-cl-pack]');
      if (pack) {
        var input = pack.querySelector('[data-basari-cl-pack-input]');
        if (input) input.value = '';
        pack.hidden = true;
        pack.style.display = 'none';
      }
      var qty = cfg.packQuantity || {};
      var eyes = cfg.eyes || { right: true, left: true };
      var right = eyes.right !== false && Number(qty.right) > 0 ? Number(qty.right) : null;
      var left = eyes.left !== false && Number(qty.left) > 0 ? Number(qty.left) : null;
      // No pack size recorded in the Basari admin: nothing is said about it.
      if (!right && !left) return;
      function per(n) {
        return n + (n === 1 ? ' lens' : ' lenses') + ' per box';
      }
      function note(text) {
        var span = document.createElement('span');
        span.className = 'basari-api-boxes-each';
        span.setAttribute('data-basari-api-pack', '');
        span.style.cssText = 'display:block;margin-top:2px;font-size:0.9em;font-weight:400;line-height:1.3;color:#6b7280;';
        span.textContent = text;
        return span;
      }
      if (root.querySelector('[data-basari-api-pack]')) return;
      if (right && left && right !== left) {
        // Each eye's own box size, under that eye's Boxes control.
        EYES.forEach(function (eye) {
          var select = inputFor('boxes', eye);
          var n = eye === 'right' ? right : left;
          if (select && select.parentNode) select.parentNode.insertBefore(note(per(n)), select.nextSibling);
        });
        return;
      }
      var label = root.querySelector('[data-basari-cl-power-row="boxes"] .basari-cl-power__row-label, [data-basari-cl-rx-field-row="boxes"] .basari-cl-power__row-label');
      if (label) {
        label.setAttribute('data-basari-api-boxes-label', '');
        var n = right || left;
        label.appendChild(note(n + (n === 1 ? ' lens' : ' lenses') + ' each'));
      }
    }

    // Without Power cannot be bought on a managed product yet (purchase()
    // refuses it), so it is not offered: the With Power choice stays selected
    // and the tabs are hidden rather than leading to a refusal at the end.
    function applyPowerModes() {
      var tabs = root.querySelector('[data-basari-cl-power-tabs]');
      if (!tabs) return;
      var withPower = tabs.querySelector('[data-basari-cl-power-tab][data-power-mode="with_power"]');
      if (!withPower) return;
      if (withPower.getAttribute('aria-selected') !== 'true') withPower.click();
      tabs.hidden = true;
    }

    // A product sold for one eye only offers just that eye.
    // The eyes this product can be bought for: the ones the admin allows AND
    // that have at least one switched-on power of their own. An eye with an
    // empty list is not offered at all, rather than shown with nothing to pick.
    function soldEyes() {
      var eyes = { right: !cfg.eyes || cfg.eyes.right !== false, left: !cfg.eyes || cfg.eyes.left !== false };
      var byEye = cfg.fieldsByEye;
      if (!byEye || isSpeciality) return eyes;
      EYES.forEach(function (eye) {
        var lists = byEye[eye] || {};
        var any = fields.some(function (f) { return lists[f] && lists[f].length > 0; });
        if (!any) eyes[eye] = false;
      });
      // Neither eye has a power: leave both, the block shows "Out of stock".
      if (!eyes.right && !eyes.left) return { right: !cfg.eyes || cfg.eyes.right !== false, left: !cfg.eyes || cfg.eyes.left !== false };
      return eyes;
    }

    function applySoldEyes() {
      var eyes = soldEyes();
      if (eyes.right && eyes.left) return;
      if (window.BasariEyeChoice && typeof window.BasariEyeChoice.restrict === 'function') {
        window.BasariEyeChoice.restrict(root.querySelector('[data-basari-cl-power]'), eyes);
      }
    }

    // The theme names each eye's controls with the bare eye key ("right
    // Spherical (SPH)"); a screen reader should hear which eye in full.
    function labelEyeControls() {
      EYES.forEach(function (eye) {
        fields.concat(['boxes']).forEach(function (field) {
          var trigger = field === 'boxes' ? inputFor('boxes', eye) : triggerFor(field, eye);
          if (trigger) trigger.setAttribute('aria-label', EYE_TITLE[eye] + ', ' + (field === 'boxes' ? 'number of boxes' : FIELD_LABEL[field]));
        });
      });
    }

    // ---- Toric Axis lock (handoff 7.3) ------------------------------------
    function applyAxisLock(eye) {
      if (lensType !== 'TORIC') return;
      var trigger = triggerFor('axis', eye);
      if (!trigger) return;
      if (!eyeTicked(eye)) {
        setHint('axis', eye, '');
        return; // the theme greys the whole column
      }
      var cyl = valueOf('cyl', eye);
      if (!cyl) {
        if (valueOf('axis', eye)) setValue('axis', eye, '');
        setLocked(trigger, true);
        setHint('axis', eye, 'Select Cylindrical first');
      } else if (isZero(cyl)) {
        setValue('axis', eye, '0');
        setLocked(trigger, true);
        setHint('axis', eye, 'No axis needed when Cylindrical is 0');
      } else {
        if (isZero(valueOf('axis', eye))) setValue('axis', eye, '');
        setLocked(trigger, false);
        setHint('axis', eye, '');
      }
    }

    // After a pick, drop any later value that no longer exists with it. Runs
    // against the backend's real combinations; a late answer for an older
    // selection is discarded.
    function syncDependents(eye) {
      if (fields.length < 2) return Promise.resolve();
      applyAxisLock(eye);
      var seq = (syncSeq[eye] += 1);
      var sel = picked(eye);
      if (!sel.sph) return Promise.resolve();

      return loadOptions(eye, { sph: sel.sph, cyl: sel.cyl }).then(
        function (byField) {
          if (destroyed || syncSeq[eye] !== seq) return;
          var cleared = [];
          if (lensType === 'TORIC') {
            var cylOpt = findOption(byField.cyl, sel.cyl);
            if (sel.cyl && (!cylOpt || !cylOpt.available)) {
              setValue('cyl', eye, '');
              setValue('axis', eye, '');
              cleared.push('Cylindrical', 'Axis');
            } else if (sel.cyl && !isZero(sel.cyl) && sel.axis && !isZero(sel.axis)) {
              var axisOpt = findOption(byField.axis, sel.axis);
              if (!axisOpt || !axisOpt.available) {
                setValue('axis', eye, '');
                cleared.push('Axis');
              }
            }
          } else if (lensType === 'MULTIFOCAL' && sel.add) {
            var addOpt = findOption(byField.add, sel.add);
            if (!addOpt || !addOpt.available) {
              setValue('add', eye, '');
              cleared.push('Addl.Power');
            }
          }
          applyAxisLock(eye);
          var first = cleared[0] === 'Cylindrical' ? 'cyl' : cleared[0] === 'Axis' ? 'axis' : 'add';
          if (cleared.length) {
            setHint(first, eye, cleared.join(' and ') + (cleared.length > 1 ? ' were' : ' was') + ' cleared: not available with this selection');
          } else if (lensType === 'MULTIFOCAL') {
            setHint('add', eye, '');
          } else if (lensType === 'TORIC') {
            setHint('cyl', eye, '');
          }
          if (cleared.length) refreshPrice();
        },
        function () {
          /* Options could not be checked now; the purchase-time quote still decides. */
        }
      );
    }

    // ---- Price -------------------------------------------------------------
    function readSelection() {
      var sel = { right: { enabled: true }, left: { enabled: true } };
      var i;
      var eyeInputs = root.querySelectorAll('[data-basari-cl-power-eye-input]');
      for (i = 0; i < eyeInputs.length; i += 1) {
        var eyeKey = eyeInputs[i].getAttribute('data-eye');
        if (sel[eyeKey]) sel[eyeKey].enabled = !!eyeInputs[i].checked;
      }
      var inputs = root.querySelectorAll('[data-basari-cl-rx-field-input]');
      for (i = 0; i < inputs.length; i += 1) {
        var field = inputs[i].getAttribute('data-field');
        var eye = inputs[i].getAttribute('data-eye');
        if (!field || !sel[eye]) continue;
        // Speciality cells are named sph/cyl in the theme, sphere/cylinder in the API.
        if (isSpeciality && field === 'sph') field = 'sphere';
        if (isSpeciality && field === 'cyl') field = 'cylinder';
        sel[eye][field] = inputs[i].value || null;
      }
      return sel;
    }

    function withoutPowerSelected() {
      var input = root.querySelector('[name="properties[power_type]"]');
      return !!input && String(input.value).toLowerCase().indexOf('without') !== -1;
    }

    function quote() {
      var sel = readSelection();
      return postJson('/api/storefront/quote', {
        shopifyProductId: shopifyProductId,
        pack: 'WITH_POWER',
        right: sel.right,
        left: sel.left
      }).then(function (result) {
        return result.ok && result.body && result.body.data ? result.body.data : null;
      });
    }

    // Before every chosen eye is priced, the product's Base Price (set in the
    // Basari admin), or when there is none, the Shopify price the theme
    // rendered, exactly as it was.
    function showBasePrice() {
      if (cfg.basePrice && Number(cfg.basePrice.amount) > 0) {
        updatePriceDisplay(root, cfg.basePrice.amount, cfg.basePrice.compareAtPrice, cfg.currency, '');
        var dom = findPriceDom(root);
        if (dom && dom.priceRoot) dom.priceRoot.setAttribute('data-basari-resolved', 'false');
      } else {
        restorePrice(root, shopifyPrice);
      }
    }

    // No. of Boxes cannot exceed the resolved SKU's stock (handoff 5.1).
    function capBoxes(view) {
      EYES.forEach(function (eye) {
        var select = inputFor('boxes', eye);
        if (!select || !select.options) return;
        var item = view.items.filter(function (i) { return i.eye === eye; })[0];
        var stock = item && item.stock != null ? Number(item.stock) : null;
        for (var i = 0; i < select.options.length; i += 1) {
          select.options[i].disabled = stock != null && stock > 0 && Number(select.options[i].value) > stock;
        }
      });
    }

    // Shows the current selection at once (price table, or the quote when it
    // has answered for exactly this selection).
    function renderNow() {
      if (destroyed) return;
      var view = buildView();
      capBoxes(view);
      renderSummary(view);
      if (view.complete) updatePriceDisplay(root, view.total, view.compareTotal, cfg.currency, '');
      else showBasePrice();
      if (!view.blocked) showMessage('');
      return view;
    }

    // Asks the backend to price the current selection; its answer replaces the
    // table's estimate. A slower, older answer never overwrites a newer one.
    function refreshPrice() {
      var seq = (priceSeq += 1);
      var key = selectionKey();
      quote().then(function (q) {
        if (destroyed || seq !== priceSeq) return;
        if (q) {
          lastQuote = q;
          lastQuoteKey = key;
        }
        try {
          root.dispatchEvent(new CustomEvent('basari:api:resolved', { bubbles: true, detail: q }));
        } catch (e) {
          /* no-op */
        }
        renderNow();
      });
    }

    // ---- Purchase ----------------------------------------------------------
    function describeErrors(q) {
      if (!q) return 'Could not check this prescription. Please try again.';
      if (!q.errors || !q.errors.length) return 'This power combination is not available';
      var seen = {};
      return q.errors
        .map(function (e) {
          var eyeName = e.eye === 'right' ? 'Right eye: ' : e.eye === 'left' ? 'Left eye: ' : '';
          return eyeName + e.message;
        })
        .filter(function (line) {
          if (seen[line]) return false;
          seen[line] = true;
          return true;
        })
        .join('\n');
    }

    // The cart line's properties in the theme's own prescription format
    // (right_eye_sph, right_eye_boxes, power_type ...), which the cart drawer,
    // the cart page and order emails already read: with the quote's own names
    // ("Eye", "SPH") the drawer showed the line with no prescription at all.
    // Display only: the backend's cart checks read _basari_sku, the variant and
    // Shopify's own price, never these names, and no price is ever sent.
    var POWER_TYPE = { 'With Power': 'with_power', 'Without Power': 'without_power', 'Special order': 'special_order' };

    // Shopify's own price for each variant of this product, read once from the
    // page's product data (/products/<handle>.js, same origin). Shopify charges
    // these prices, never the quote's, so a power whose mapped variant Shopify
    // sells at a different price is shown as not orderable online and is not
    // put in the cart at all (instead of being added and taken back out).
    var PRICE_DIFFERS = 'This item cannot be ordered online right now. Please contact us to order.';
    var shopifyPrices = null;
    var shopifyVariants = null;
    var shopifyPricesLoad = null;
    function loadShopifyPrices() {
      if (shopifyPricesLoad) return shopifyPricesLoad;
      var m = String(window.location.pathname).match(/\/products\/([^/?#]+)/);
      shopifyPricesLoad = !m
        ? Promise.resolve(null)
        : fetch(cartRoot() + 'products/' + m[1] + '.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
            .then(function (res) {
              return res.ok ? res.json() : null;
            })
            .then(function (product) {
              if (!product || !product.variants || String(product.id) !== String(shopifyProductId)) return null;
              shopifyVariants = product.variants;
              var out = {};
              product.variants.forEach(function (v) {
                out[String(v.id)] = Number(v.price) / 100;
              });
              shopifyPrices = out;
              renderNow();
              return out;
            })
            .catch(function () {
              return null;
            });
      return shopifyPricesLoad;
    }
    function priceDiffers(line) {
      var shopifyPrice = shopifyPrices && line && line.variantId ? shopifyPrices[String(line.variantId)] : undefined;
      return shopifyPrice != null && Math.abs(shopifyPrice - Number(line.unitPrice)) > 0.005;
    }
    function cartProperties(line) {
      var src = line.properties || {};
      var eye = line.eye === 'right' || line.eye === 'left' ? line.eye : null;
      if (!eye) return src;
      var out = {};
      Object.keys(src).forEach(function (key) {
        var value = src[key];
        if (key === 'Eye') return;
        if (key === 'Pack') {
          out.power_type = POWER_TYPE[value] || String(value).toLowerCase().replace(/\s+/g, '_');
          return;
        }
        if (key.charAt(0) === '_' || key === 'Lens type') {
          out[key] = value;
          return;
        }
        out[eye + '_eye_' + key.toLowerCase().replace(/\s+/g, '_')] = value;
      });
      out[eye + '_eye_boxes'] = String(line.quantity);
      return out;
    }

    // ---- Draft Order cart lines --------------------------------------------
    // The Shopify variant the line sits on. Its price is NOT what is charged
    // (the Draft Order carries the backend's price); it only puts the line in
    // Shopify's cart so the drawer and cart page can show and edit it.
    function baseVariantId() {
      var variants = shopifyVariants || [];
      if (variants.length === 1) return variants[0].id;
      var input = root.querySelector('[name="id"]') || document.querySelector('form[action*="/cart/add"] [name="id"]');
      if (input && input.value) return input.value;
      var available = variants.filter(function (v) { return v.available; })[0] || variants[0];
      return available ? available.id : null;
    }

    // What the backend re-checks at payment: the eye and the values as picked.
    // A claim, never trusted: the server resolves it to a SKU and price again.
    function rxClaim(eye) {
      var sel = readSelection()[eye] || {};
      var claim = { e: eye };
      ['sph', 'cyl', 'axis', 'add', 'sphere', 'cylinder'].forEach(function (k) {
        if (sel[k] != null && sel[k] !== '') claim[k] = String(sel[k]);
      });
      return JSON.stringify(claim);
    }

    function addForDraftOrder(q) {
      var variantId = baseVariantId();
      if (!variantId) throw new Error('This product cannot be added to the cart right now. Please refresh the page.');
      var items = q.lines.map(function (line) {
        var props = cartProperties(line);
        props._basari_rx = rxClaim(line.eye);
        // Shown in the review cart until the backend prices the cart again.
        props._basari_price = Number(line.unitPrice).toFixed(2);
        return { id: Number(variantId), quantity: line.quantity, properties: props };
      });
      return fetch(cartRoot() + 'cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items })
      }).then(function (res) {
        return res.json().then(function (payload) {
          if (!res.ok) throw new Error(payload.description || payload.message || 'Could not add to cart.');
          return payload;
        });
      });
    }

    function purchase(intent, trigger) {
      // A second click while the first is in flight does nothing.
      if (busy) return;
      if (withoutPowerSelected()) {
        showMessage('Without Power is not available for this product yet. Choose With Power.');
        return;
      }
      busy = true;
      var pending = [];
      if (trigger) trigger.setAttribute('aria-busy', 'true');
      showMessage('');

      var draft = draftMode();
      // The quote is fetched again here, at the moment of purchase: it is the
      // final server-side check of prescription, status, stock and mapping.
      Promise.all([quote(), loadShopifyPrices()])
        .then(function (results) {
          var q = results[0];
          if (!q || !q.valid) throw new Error(describeErrors(q));
          if (!q.purchasable) {
            throw new Error('This prescription cannot be ordered online yet. Please contact us to order.');
          }
          if (draft) return addForDraftOrder(q);
          // Shopify would charge its own, different price: nothing is added.
          if (q.lines.some(priceDiffers)) throw new Error(PRICE_DIFFERS);
          pending = q.lines;
          var items = q.lines.map(function (line) {
            // Only the variant, quantity and readable properties are sent.
            // The price is never sent: Shopify applies the variant's own.
            return { id: Number(line.variantId), quantity: line.quantity, properties: cartProperties(line) };
          });
          return fetch(cartRoot() + 'cart/add.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: items })
          }).then(function (res) {
            return res.json().then(function (payload) {
              // Shopify's own refusal (e.g. its stock ran out) carries a description.
              if (!res.ok) throw new Error(payload.description || payload.message || 'Could not add to cart.');
              return payload;
            });
          });
        })
        .then(function (payload) {
          // A Draft Order line is priced by the backend, not by this variant.
          if (draft) return payload;
          // Shopify charges the variant's own price. If a variant was mapped
          // but its price in Shopify differs from the SKU's price here, take
          // the lines back out rather than let the shopper pay the wrong amount.
          var wrong = (payload.items || []).filter(function (item) {
            var line = pending.filter(function (l) {
              return String(l.variantId) === String(item.variant_id);
            })[0];
            return line && Math.abs(item.original_price / 100 - line.unitPrice) > 0.005;
          });
          if (!wrong.length) return payload;
          var updates = {};
          wrong.forEach(function (item) {
            updates[item.key] = 0;
          });
          return fetch(cartRoot() + 'cart/update.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ updates: updates })
          }).then(function () {
            throw new Error('This item cannot be ordered online right now. Please contact us to order.');
          });
        })
        .then(function () {
          if (intent === 'buy-now') {
            // Draft Order: Shopify's /checkout would charge the product's own
            // variant price, so Buy Now goes to the Basari payment step, which
            // keeps the cart and stays on the page if anything fails.
            if (draft) {
              var pay = window.BasariCheckout;
              if (pay && typeof pay.start === 'function') return pay.start(trigger);
              window.location.href = (window.routes && window.routes.cart_url) || '/cart';
              return;
            }
            window.location.href = '/checkout';
            return;
          }
          var premium = window.PremiumCart;
          if (premium && typeof premium.refresh === 'function' && typeof premium.open === 'function') {
            Promise.resolve(premium.refresh()).then(function () {
              premium.open(trigger);
            });
          } else {
            window.location.href = (window.routes && window.routes.cart_url) || '/cart';
          }
        })
        .catch(function (err) {
          showMessage(err && err.message ? err.message : 'Could not add to cart.');
        })
        .then(function () {
          busy = false;
          if (trigger) trigger.removeAttribute('aria-busy');
        });
    }

    // ---- Wiring ------------------------------------------------------------
    var debouncedQuote = debounce(refreshPrice, DEBOUNCE_MS);
    // A change is shown at once from the price table; the quote follows.
    function debouncedRefresh() {
      renderNow();
      debouncedQuote();
    }

    on(root, 'basari:cl:rx-change', function (event) {
      var detail = event.detail || {};
      if (detail.eye && (detail.field === 'sph' || detail.field === 'cyl')) syncDependents(detail.eye);
      if (detail.eye && detail.field && detail.field !== 'boxes') setHint(detail.field, detail.eye, detail.field === 'axis' ? '' : null);
      debouncedRefresh();
    });
    ['basari:cl:eye-toggle', 'basari:cl:power-mode'].forEach(function (name) {
      on(root, name, function () {
        // After the theme has finished enabling or disabling the column.
        window.setTimeout(function () {
          EYES.forEach(applyAxisLock);
        }, 0);
        debouncedRefresh();
      });
    });
    on(root, 'basari:cl:boxes-change', debouncedRefresh);
    // Speciality cells are plain inputs with no custom event.
    on(root, 'change', function (event) {
      if (isSpeciality && event.target && event.target.hasAttribute && event.target.hasAttribute('data-basari-cl-rx-field-input')) {
        debouncedRefresh();
      }
    });

    watchModals();
    applyPowerModes();
    applyPackInfo();
    applySoldEyes();
    labelEyeControls();
    EYES.forEach(applyAxisLock);

    if (cfg.outOfStock) {
      // Whole family out of stock: nothing can be bought (handoff 10.5).
      showMessage('Out of stock');
      [root.querySelector('[data-basari-cl-buy-now]'), root.querySelector('[data-basari-add-cart]')].forEach(function (btn) {
        setLocked(btn, true);
      });
    }
    renderNow();
    refreshPrice();
    loadShopifyPrices();

    return {
      root: root,
      purchase: purchase,
      // Subscribe lens goes through the theme's Recharge flow, which adds the
      // product's generic variant: one line for both eyes, at that variant's
      // price. For a managed product that is the wrong SKU and the wrong price,
      // so it is refused here until subscriptions exist on the per-SKU variants.
      refuseSubscribe: function () {
        showMessage('Subscriptions are not available online for this product yet. Use Buy Now, or contact us to subscribe.');
      },
      // Fresher product data from the backend (the page was set up from this
      // session's copy): prices and settings follow it at once.
      setConfig: function (fresh) {
        Object.keys(cfg).forEach(function (k) { delete cfg[k]; });
        Object.keys(fresh).forEach(function (k) { cfg[k] = fresh[k]; });
        applyPowerModes();
        applyPackInfo();
        applySoldEyes();
        renderNow();
        refreshPrice();
      },
      // The product is no longer managed: everything added here is taken back.
      detach: function () {
        this.destroy();
        var box = root.querySelector('[data-basari-api-summary]');
        if (box && box.parentNode) box.parentNode.removeChild(box);
        var notes = root.querySelectorAll('[data-basari-api-pack]');
        for (var i = 0; i < notes.length; i += 1) notes[i].parentNode.removeChild(notes[i]);
        showMessage('');
        restorePrice(root, shopifyPrice);
      },
      destroy: function () {
        destroyed = true;
        observers.forEach(function (o) { o.disconnect(); });
        listeners.forEach(function (l) { l[0].removeEventListener(l[1], l[2], l[3]); });
      }
    };
  }

  // A re-rendered section replaces its root element. Drop controllers whose
  // root has left the page so their listeners and observers go with them.
  function pruneControllers() {
    controllers = controllers.filter(function (c) {
      if (document.documentElement.contains(c.root)) return true;
      c.destroy();
      return false;
    });
  }

  function controllerFor(instance) {
    var root = instance && instance.root;
    for (var i = 0; i < controllers.length; i += 1) {
      if (controllers[i].root === root) return controllers[i];
    }
    return null;
  }

  // Wrap the theme's purchase hooks once. For a root this script manages, our
  // flow runs; for every other root the theme's original function runs untouched.
  function installHooks() {
    var api = window.BasariContactLens;
    if (!api || !api.extensions || api.extensions.__basariApiWrapped) return;
    var ext = api.extensions;
    var originalBuyNow = ext.buyNow;
    var originalAddToCart = ext.addToCart;

    ext.buyNow = function (instance) {
      var c = controllerFor(instance);
      if (c) return c.purchase('buy-now', instance.dom && instance.dom.buyNow);
      return typeof originalBuyNow === 'function' ? originalBuyNow.apply(this, arguments) : undefined;
    };
    ext.addToCart = function (instance, options) {
      var c = controllerFor(instance);
      if (c) {
        // The theme validates before Buy Now but not before the cart icon,
        // so run its required-field validation here as well.
        if (typeof ext.validate === 'function' && !ext.validate(instance)) return undefined;
        var intent = options && options.intent === 'buy-now' ? 'buy-now' : 'add-to-cart';
        return c.purchase(intent, instance.dom && instance.dom.addToCart);
      }
      return typeof originalAddToCart === 'function' ? originalAddToCart.apply(this, arguments) : undefined;
    };
    var originalSubscribe = ext.openSubscribeModal;
    ext.openSubscribeModal = function (instance) {
      var c = controllerFor(instance);
      if (c) return c.refuseSubscribe();
      return typeof originalSubscribe === 'function' ? originalSubscribe.apply(this, arguments) : undefined;
    };
    ext.__basariApiWrapped = true;
  }

  // The backend could not be reached for a product this browser has seen as
  // managed before. Selling it through the theme's generic variant would
  // charge the wrong price, so purchase is held until the backend answers.
  function holdPurchases(root, shopifyProductId) {
    var box = document.createElement('p');
    box.setAttribute('data-basari-api-message', '');
    box.setAttribute('role', 'alert');
    box.style.cssText = 'margin:8px 0 0;font-size:13px;line-height:1.4;color:#b42318;';
    box.textContent = 'Prices could not be loaded. ';
    var retry = document.createElement('button');
    retry.type = 'button';
    retry.setAttribute('data-basari-api-retry', '');
    retry.textContent = 'Try again';
    retry.style.cssText = 'padding:2px 10px;border:1px solid #9ca3af;border-radius:8px;background:#fff;font-size:13px;cursor:pointer;';
    box.appendChild(retry);
    (root.querySelector('[data-basari-cl-purchase]') || root).appendChild(box);

    function block(event) {
      var hit = event.target instanceof Element ? event.target.closest('[data-basari-cl-buy-now], [data-basari-add-cart]') : null;
      if (!hit) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    }
    root.addEventListener('click', block, true);
    retry.addEventListener('click', function () {
      root.removeEventListener('click', block, true);
      box.parentNode.removeChild(box);
      root.removeAttribute('data-basari-api-ready');
      delete configCache[shopifyProductId];
      initRoot(root);
    });
  }

  function remember(shopifyProductId, managed) {
    try {
      if (managed) window.localStorage.setItem(MANAGED_KEY + shopifyProductId, '1');
      else window.localStorage.removeItem(MANAGED_KEY + shopifyProductId);
    } catch (e) {
      /* storage unavailable */
    }
  }
  function wasManaged(shopifyProductId) {
    try {
      return window.localStorage.getItem(MANAGED_KEY + shopifyProductId) === '1';
    } catch (e) {
      return false;
    }
  }

  // The product data last received, per product, for this browser session:
  // a returning shopper's page is set up at once and refreshed in the
  // background. Purchase never relies on it (the quote is asked at the click).
  var SESSION_KEY = 'basari:cfg:';
  var SESSION_TTL_MS = 10 * 60 * 1000;
  function cachedConfig(shopifyProductId) {
    try {
      var entry = JSON.parse(window.sessionStorage.getItem(SESSION_KEY + shopifyProductId) || 'null');
      return entry && Date.now() - entry.at < SESSION_TTL_MS ? entry.data : null;
    } catch (e) {
      return null;
    }
  }
  function storeConfig(shopifyProductId, data) {
    try {
      if (data) window.sessionStorage.setItem(SESSION_KEY + shopifyProductId, JSON.stringify({ at: Date.now(), data: data }));
      else window.sessionStorage.removeItem(SESSION_KEY + shopifyProductId);
    } catch (e) {
      /* storage unavailable */
    }
  }

  // A product this browser has seen as managed: the theme's pack block and
  // power tabs are hidden at once, before the backend answers, so they never
  // flash on screen. Undone if the backend says the product is not managed.
  function prehide(root) {
    var nodes = root.querySelectorAll('[data-basari-cl-pack], [data-basari-cl-power-tabs]');
    var done = [];
    for (var i = 0; i < nodes.length; i += 1) {
      if (nodes[i].hidden) continue;
      nodes[i].hidden = true;
      nodes[i].style.display = 'none';
      done.push(nodes[i]);
    }
    return function undo() {
      done.forEach(function (n) {
        n.hidden = false;
        n.style.display = '';
      });
    };
  }

  function attach(root, shopifyProductId, cfg) {
    // Not managed, or a colour family (left to the theme for now): do nothing.
    if (!cfg || cfg.lensCategory !== 'CLEAR') return null;
    installHooks();
    var controller = createController(root, shopifyProductId, cfg);
    controllers.push(controller);
    return controller;
  }

  function initRoot(root) {
    if (!API_BASE_URL) return;
    if (!root || root.getAttribute('data-basari-api-ready') === 'true') return;
    root.setAttribute('data-basari-api-ready', 'true');
    pruneControllers();

    var shopifyProductId = String(parseConfigJson(root).productId || '');
    if (!shopifyProductId) return;

    if (Object.prototype.hasOwnProperty.call(configCache, shopifyProductId)) {
      attach(root, shopifyProductId, configCache[shopifyProductId]);
      return;
    }
    var cached = cachedConfig(shopifyProductId);
    var undo = cached || wasManaged(shopifyProductId) ? prehide(root) : null;
    var controller = cached ? attach(root, shopifyProductId, cached) : null;

    fetchJson(API_BASE_URL + '/api/storefront/products/' + encodeURIComponent(shopifyProductId)).then(function (result) {
      if (result.ok && result.body && result.body.data) {
        configCache[shopifyProductId] = result.body.data;
        remember(shopifyProductId, true);
        storeConfig(shopifyProductId, result.body.data);
        if (controller) controller.setConfig(result.body.data);
        else attach(root, shopifyProductId, result.body.data);
      } else if (result.status === 404 && result.body && result.body.error && result.body.error.code === 'NOT_MANAGED') {
        // The backend itself says this product is not priced there: give the
        // page back to the theme. Any other 404 (a wrong address, a tunnel
        // that is offline) is "unreachable", never "not managed".
        configCache[shopifyProductId] = null;
        remember(shopifyProductId, false);
        storeConfig(shopifyProductId, null);
        if (controller) {
          controller.detach();
          controllers = controllers.filter(function (c) { return c !== controller; });
        }
        if (undo) undo();
      } else {
        // Say why in the console, for whoever is looking at the page. Status 0
        // = no answer the browser may read: offline service, wrong address,
        // or a CORS refusal (the backend's CORS_ALLOWED_ORIGINS).
        if (window.console) {
          console.warn('[Basari] Prices could not be loaded from ' + API_BASE_URL + ' (' + (result.status ? 'HTTP ' + result.status : 'no answer: service offline, wrong address or CORS') + ').');
        }
        if (!controller && wasManaged(shopifyProductId)) holdPurchases(root, shopifyProductId);
      }
      // Otherwise the backend is unreachable: a page set up from this session's
      // data keeps working (purchase still asks the backend and is refused if
      // it cannot answer), and a product never seen as managed is left to the theme.
    });
  }

  function initAll(scope) {
    var host = scope || document;
    var roots = host.querySelectorAll ? host.querySelectorAll('[data-basari-contact-lens]') : [];
    for (var i = 0; i < roots.length; i += 1) initRoot(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    var section = event.target;
    if (section && section.querySelector && section.querySelector('[data-basari-contact-lens]')) initAll(section);
  });
})();
